<template>
	<Disclosure class="TicketInfoNew" v-if="modelValue"
		ref="SECTION_ticketInfo"
		:title="$t('text.tickets')"
		:lock="productIsExternal || locked"
		:error="sectionMessage.error"
		:warning="sectionMessage.warning"
		:message="sectionMessage.message"
		data-cy="ticketInfo"
	>
		<Field :title="$t('text.taxClass')" :helpText="$t('text.taxClassDesc')" required
			v-if="hasField('ticketInfo','taxClass', modelValue)"
		>
			<TaxClassSelect v-model="modelValue.fields.taxClass.de" :disabled="productIsExternal" />
		</Field>
		<TicketInfoExternalSystem v-if="hasField('ticketInfo','externalSystem', modelValue) && modelValue.fields.ticket.de.fields?.external?.system"
			:modelValue="modelValue"
			:disabled="productIsExternal"
		/>
		<Field :title="$t('text.tickets')" :title-flex="true" required>
			<template #titleExtra>
				<RefreshButton
					:tooltip="$t('text.refreshTicketTypesAndReductions')"
					:method-to-call="refreshTicketTypesAndReductions"
					:disabled="productIsExternal"
				/>
			</template>
			<div v-if="hasField('ticketInfo', 'fromPrice', modelValue)" class="stays-editable-when-locked" style="display: flex; gap: 10px;">
				<p class="helpText" v-html="$t('text.fromPriceDesc')" />
				<mys-switch v-model="modelValue.fields.fromPriceEnabled.de"
					:label="$t('text.fromPrice')"
					data-cy="fromPrice"
					style="flex-shrink: 0;"
				/>
			</div>
			<p class="helpText" v-if="ticketOptionsAreReorderable" v-html="$t('text.ticketOptionOrderDesc')" />
			<draggable class="ticketOptions" v-if="modelValue.fields.ticket?.de?.fields?.ticketOptions?.de"
				:list="modelValue.fields.ticket.de.fields.ticketOptions.de"
				:disabled="!ticketOptionsAreReorderable"
				handle=".dragHandle"
				@change="onTicketOptionMoved"
			>
				<TicketOption v-for="(ticketOption, ti) in modelValue.fields.ticket.de.fields.ticketOptions.de" :key="`${ticketOption.sys.id ? ticketOption.sys.id : ticketOption.key}`"
					:ref="'ticketOption' + ti"
					:modelValue="ticketOption"
					:product="modelValue"
					:externalSystem="modelValue.fields.ticket.de.fields?.external?.system"
					:productIsExternal="productIsExternal"
					:dataCyIndex="ti"
					:allowDelete="!productIsExternal && modelValue.fields.ticket.de.fields.ticketOptions.de.length > 1"
					:draggable="ticketOptionsAreReorderable"
					:ticketOptionAssignment="ticketOptionAssignments.length ? ticketOptionAssignments[ti] : null"
					@delete="removeTicketOption(ti)"
					@fromPriceChanged="onFromPriceChanged($event, ti)"
					@updateTicketOptionAssignment="updateTicketOptionAssignment($event, ti)"
					@removeTicketOptionAssignment="removeTicketOptionAssignment(ti)"
				/>
			</draggable>
			<button v-if="!productIsExternal" class="addTicketOptions addPanel" elevation="0" @click="addTicketOption" data-cy="addTicketOption">
				<v-icon>mdi-plus</v-icon>
				{{ $t('text.addTicketOption') }}
			</button>
		</Field>
	</Disclosure>
</template>

<script lang="ts">
import Disclosure from '../../components/common/Disclosure.vue'
import TicketTypeField from './TicketTypeField.vue'
import ReductionField from './ReductionField.vue'
import TaxClassSelect from './TaxClassSelect.vue'
import TicketOption from './TicketOption.vue'
import MysSwitch from '../mys/mys-switch.vue'
import TicketInfoExternalSystem from './TicketInfoExternalSystem.vue'
import Common from '../../mixins/Common.vue'
import RefreshButton from '../common/RefreshButton.vue'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import TicketAssignmentExecutive from '../../../../api2/src/modules/ticket-assignment/TicketAssignmentExecutive'
import isEqual from 'lodash/isEqual'
import { EXTERNAL_SYSTEM } from '../../constants'
import { PriceType } from '../../constants'

// TODO: hasField

export default {
	components: { Disclosure, TicketTypeField, ReductionField, TaxClassSelect, TicketOption, MysSwitch, TicketInfoExternalSystem, RefreshButton, draggable: draggable as any },
	props: {
		modelValue: Object, // Product
		locked: Boolean,
	},
	mixins: [Common],
	data: () => ({
		// TODO: we should get rid of this and use our <Section> comp instead
		sectionMessage: {
			error: false,
			warning: false,
			message: '',
		},
		ticketOptionAssignments: [] as any[],
		originalTicketOptionAssignments: [] as any[]
	}),
	computed: {
		// TODO: do we really need this? or is the whole ticket section disabled for externals anyhow?
		productIsExternal() {
			return this.modelValue?.addl?.isExternal
		},
		valid() {
			return this.validateAllFields()
		},
		ticketOptionsAreReorderable() {
			if (this.productIsExternal) return false
			return (this.modelValue?.fields?.ticket?.de?.fields?.ticketOptions?.de?.length ?? 0) > 1
		},
	},
	watch: {
		modelValue: {
			immediate: true,
			deep: true,
			handler() {
				this.$emit('change')
				this.emitTicketOptions()
			},
		},
	},
	methods: {
		emitTicketOptions() {
			const ticketOptions = this.modelValue?.fields?.ticket?.de?.fields?.ticketOptions?.de ?? []
			const payload = ticketOptions
				.map(option => ({
					ticketOptionId: option.sys?.id ? option.sys?.id : option.key,
					ticketTypeId: option.fields?.ticketType?.de?.sys?.id ?? null,
					ticketTypeTitle: option.fields?.ticketType?.de?.fields?.title?.de ?? null,
					reductionId: option.fields?.reduction?.de?.sys?.id ?? null,
					reductionTitle: option.fields?.reduction?.de?.fields?.title?.de ?? null,
					price: option.fields?.price?.de ?? null
				}))
				.filter(option => option.ticketTypeId && option.reductionId)
			this.$emit('ticket-options-change', payload)
		},
		normalizeTicketOptionsForUi() {
			const ticketOptions = this.modelValue?.fields?.ticket?.de?.fields?.ticketOptions?.de ?? []
			const externalSystem = this.modelValue?.fields?.ticket?.de?.fields?.external?.system

			for (const ticketOption of ticketOptions) {
				if (!ticketOption?.fields) {
					continue
				}

				if (!ticketOption.fields.fromPriceOption) {
					ticketOption.fields.fromPriceOption = { de: false }
				}

				const price = ticketOption.fields?.price?.de
				if (typeof price === 'string') {
					const normalizedPrice = parseFloat(price.replace(',', '.'))
					if (!Number.isNaN(normalizedPrice)) {
						ticketOption.fields.price.de = normalizedPrice
					}
				}

				const external = ticketOption.fields.external ?? {}
				switch (externalSystem) {
				case EXTERNAL_SYSTEM.SKIDATA:
					ticketOption.fields.external = {
						dtaProductId: external.dtaProductId ?? '',
						dtaConsumerId: external.dtaConsumerId ?? '',
						dtaCustomerConfig: external.dtaCustomerConfig ?? '',
					}
					break
				case EXTERNAL_SYSTEM.AXESS:
					ticketOption.fields.external = {
						ticketTypeNumber: external.ticketTypeNumber ?? '',
						poolNumber: external.poolNumber ?? '',
						personTypeNumber: external.personTypeNumber ?? '',
						idNumber: external.idNumber ?? '',
					}
					break
				case EXTERNAL_SYSTEM.SBBV3:
					ticketOption.fields.external = {
						id1: external.id1 ?? '',
						id2: external.id2 ?? '',
						id3: external.id3 ?? '',
						duration: external.duration ?? '',
						reductionClass: external.reductionClass ?? '',
					}
					break
				case EXTERNAL_SYSTEM.SMEETZ:
					ticketOption.fields.external = {
						supplierId: external.supplierId ?? '',
						activityId: external.activityId ?? '',
						optionId: external.optionId ?? '',
						ticketCategoryId: external.ticketCategoryId ?? '',
					}
					break
				case EXTERNAL_SYSTEM.INFOSYSTEM:
					ticketOption.fields.external = {
						isTransportationProduct: external.isTransportationProduct ?? false,
						categoryId: external.categoryId ?? null,
						productId: external.productId ?? null,
						externalProductReference: external.externalProductReference ?? null,
						fromStation: external.fromStation ?? null,
						toStation: external.toStation ?? null,
					}
					break
				default:
					break
				}
			}
		},
		async persistTicketOptionAssignment() {
			if (!isEqual(this.ticketOptionAssignments, this.originalTicketOptionAssignments)) {
				const ticketAssignmentExecutive = new TicketAssignmentExecutive(this)
				const ticketOptions = this.modelValue.fields.ticket?.de?.fields?.ticketOptions?.de ?? []
				const clientId = this.$store.state.selectedClient.sys.id
				const serviceProviderId = this.$store.state.selectedServiceProvider.sys.id
				// not sure if this is safe, would be better if we would have "dummyIds", but this do not get saved, when model is updated
				// also, how would we after save match the dummyId with real ids, based on what? What if one ticketOption is not saved correctly?
				for (const [index, ticketOption] of ticketOptions.entries()) {
					const assignment = this.ticketOptionAssignments[index]
					if (Object.keys(assignment).length > 0) {
						if (!assignment.clientId) {
							await ticketAssignmentExecutive.getOrCreateAssignment(this.modelValue.sys.id, clientId, serviceProviderId, ticketOption.sys.id)
						}
						await ticketAssignmentExecutive.updateAssignment(this.modelValue.sys.id, clientId, serviceProviderId, ticketOption.sys.id, assignment.salesChannelIds, assignment.resellerCategoryIds)
					} else {
						await ticketAssignmentExecutive.removeAssignment(this.modelValue.sys.id, clientId, serviceProviderId, ticketOption.sys.id)
					}
				}
				const key = a => JSON.stringify([a.productId, a.clientId, a.ticketOptionId])
				const current = new Set(this.ticketOptionAssignments.filter(x => Object.keys(x).length).map(key))
				const toDelete = this.originalTicketOptionAssignments.filter(x => Object.keys(x).length && !current.has(key(x)))
				for (const assignment of toDelete) {
					await ticketAssignmentExecutive.removeAssignment(assignment.productId, assignment.clientId, serviceProviderId, assignment.ticketOption)
				}
				this.getSalesChannelAssignments()
			}
		},
		removeTicketOptionAssignment(index) {
			this.ticketOptionAssignments[index] = {}
		},
		updateTicketOptionAssignment(value, index) {
			this.ticketOptionAssignments[index] = {
				...this.ticketOptionAssignments[index],
				salesChannelIds: value?.[0]?.selected?.filter(x => x.id !== 'dummy')?.map(x => x.id),
				resellerCategoryIds: value?.[0]?.selected?.filter(x => x.id !== 'dummy')?.flatMap(x => x.selected?.filter(x => x.id !== 'dummy'))
			}
		},
		async getSalesChannelAssignments() {
			this.ticketOptionAssignments = []
			const ticketAssignmentExecutive = new TicketAssignmentExecutive(this)
			const productId = this.modelValue?.sys?.id
			const clientId = this.$store.state.selectedClient.sys.id
			const serviceProviderId = this.$store.state.selectedServiceProvider.sys.id
			let assignments: any[] = []
			if (productId && clientId) {
				assignments = await ticketAssignmentExecutive.getProductAssignments(productId, clientId, serviceProviderId)
			}
			const ticketOptions: any[] = this.modelValue.fields.ticket.de.fields.ticketOptions.de
			for (const ticketOption of ticketOptions) {
				let foundAssignment = assignments.find(a => a.ticketOptionId === ticketOption.sys.id)
				if (!foundAssignment) {
					foundAssignment = {} as any
				}
				this.ticketOptionAssignments.push(foundAssignment)
			}
			this.originalTicketOptionAssignments = JSON.parse(JSON.stringify(this.ticketOptionAssignments))
		},
		// The ticket options themselves are reordered in place by draggable (:list). The
		// assignments are kept in a separate index parallel array, so they have to follow the
		// same move or every reorder would silently reassign sales channels to another option.
		onTicketOptionMoved(event) {
			const moved = event?.moved
			if (!moved) return

			const { oldIndex, newIndex } = moved
			if (oldIndex === newIndex) return
			if (oldIndex == null || newIndex == null) return
			if (!this.ticketOptionAssignments.length) return

			const [assignment] = this.ticketOptionAssignments.splice(oldIndex, 1)
			this.ticketOptionAssignments.splice(newIndex, 0, assignment)
		},
		removeTicketOption(index) {
			this.modelValue.fields.ticket.de.fields.ticketOptions.de.splice(index, 1)
			this.ticketOptionAssignments.splice(index, 1)
		},
		addTicketOption() {
			if (!this.modelValue?.fields?.ticket?.de?.fields?.ticketOptions?.de) throw new Error('modelValue is not properly set up')
			const ticketOptions: any[] = this.modelValue.fields.ticket.de.fields.ticketOptions.de
			const ticketPersonalisations = ticketOptions?.[0]?.fields?.ticketPersonalisations?.de ?? []
			
			// For InfoSystem products, copy external fields from the last ticket option
			const externalSystem = this.modelValue.fields.ticket.de.fields?.external?.system
			let externalFields = {}
			if (externalSystem === EXTERNAL_SYSTEM.INFOSYSTEM && ticketOptions.length > 0) {
				const lastTicketOption = ticketOptions[ticketOptions.length - 1]
				if (lastTicketOption.fields.external) {
					// Deep copy the external fields
					externalFields = {
						isTransportationProduct: lastTicketOption.fields.external.isTransportationProduct ?? false,
						categoryId: lastTicketOption.fields.external.categoryId ?? '',
						productId: lastTicketOption.fields.external.productId ?? '',
						externalProductReference: lastTicketOption.fields.external.externalProductReference ?? '',
						fromStation: lastTicketOption.fields.external.fromStation ?? '',
						toStation: lastTicketOption.fields.external.toStation ?? '',
					}
				}
			}

			// TODO: do we actually need to set an id? we usually only do this when we reference to it - but where do we?
			//const maxIdNr = ticketOptions
			//	.map(to => to.sys.id)
			//	.filter(id => id.startsWith('id_v'))
			//	.map(id => parseInt(id.replace('id_v', '')))
			//	.reduce((r, id) => Math.max(r, id), 0)
			const id = '' //'id_v' + (maxIdNr + 1)
			ticketOptions.push({
				sys: { id },
				fields: {
					title: { de: '' },
					ticketType: { de: null },
					reduction: { de: null },
					price: { de: '' },
					ticketPersonalisations: { de: ticketPersonalisations },
					minimumTickets: { de: 1 },
					maximumTickets: { de: 6 },
					contingentQuantity: { de: 1 },
					fromPriceOption: { de: false },
					priceType: { de: PriceType.GENERIC },
					external: externalFields,
				},
				key: Math.random()
			})
			this.ticketOptionAssignments.push({})
		},
		// TODO: use childErrorDispatcher / Receiver instead
		validateAllFields() {
			this.resetSectionError(this.sectionMessage)
			if (!this.modelValue) return false
			if (this.hasField('ticketInfo', 'taxClass', this.modelValue) && !this.modelValue.fields.taxClass.de?.sys?.id) {
				this.setSectionError(this.sectionMessage, this.$t('text.taxClassRequired'))
				return false
			}
			const ticket = this.modelValue.fields.ticket.de
			const ticketOptions = ticket.fields.ticketOptions.de || []
			for (let to = 0; to < ticketOptions.length; to++) {
				const ticketOptionRef = this.$refs['ticketOption' + to]?.[0]
				if (!ticketOptionRef) continue
				if (!ticketOptionRef.valid) {
					this.setSectionError(this.sectionMessage, this.$t('text.ticketOptionsInvalidError'))
					return false
				}
			}
			return true
		},
		validateReductions() {
			// Multiple free-tour-guide reductions are allowed across ticket options.
			return true
		},
		validateCombinations() {
			return true
		},
		onFromPriceChanged(value, ticketOptionIndex) {
			if (!value) return
			this.modelValue.fields.ticket.de.fields.ticketOptions.de.forEach((ticketOption, index) => {
				if (index === ticketOptionIndex) return
				ticketOption.fields.fromPriceOption.de = false
			})
		},
		async refreshTicketTypesAndReductions() {
			// Fetch fresh ticket types and reductions from server
			const clientId = this.$store.state.selectedClient.sys.id
			const locale = this.selectedLocale
			
			try {
				const response = await this.$httpGet(`/client-options-refresh?clientId=${clientId}&locale=${locale}`)
				
				// method to clean the data to remove any non-serializable properties
				const cleanData = (obj) => JSON.parse(JSON.stringify(obj))
				
				// Clean the entire client object first to remove any existing non-serializable data - for some reason the indexDB throws an error if we don't do this
				const cleanClient = cleanData(this.$store.state.selectedClient)
				
				if (response.ticketTypes) {
					cleanClient.fields.ticketTypes = { de: response.ticketTypes }
				}
				
				if (response.reductions) {
					cleanClient.fields.reductions = { de: response.reductions }
				}
				
				const defaultAssignments = []
				if (response.ticketTypes) {
					defaultAssignments.push(...response.ticketTypes.filter(option => option.defaultAssignment))
				}
				if (response.reductions) {
					defaultAssignments.push(...response.reductions.filter(option => option.defaultAssignment))
				}
				cleanClient.fields.defaultAssignments = { de: defaultAssignments }

				await this.$store.commit('setSelectedClient', cleanClient)
				
			} catch (error) {
				console.error('Error refreshing tickets:', error)
				throw error // Let RefreshButton handle the error display
			}
		},
	},
	beforeMount() {
		if (!this.modelValue) return
		if (!this.modelValue.fields.fromPriceEnabled)
			this.modelValue.fields.fromPriceEnabled = { de: false }
		if (!this.modelValue.fields.ticket?.de?.fields?.ticketOptions?.de || this.modelValue.fields.ticket.de.error)
			this.modelValue.fields.ticket = { de: { sys: { id: '' }, fields: { ticketOptions: { de: [] } } } }
		if (this.$store.state.featureFlags['FEATURE_SALES_CHANNEL_ASSIGNMENT'] && !this.featureEnabled('ticket-option-assignment')) {
			this.getSalesChannelAssignments()
		}
		if (this.hasField('ticketInfo','externalSystem', this.modelValue) && !this.modelValue.fields.ticket.de.fields?.external){
			this.modelValue.fields.ticket.de.fields.external = { system: 'Skidata' }
		}
		this.normalizeTicketOptionsForUi()
	},
	}
</script>

<style scoped>
.ticketOptions { display: flex; flex-direction: column; gap: 12px; margin-top: 12px; }
.addTicketOptions { margin-top: 12px; }
</style>
